import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";

export const INDIVIDUAL_BOOKMARK_QUERY_MAXIMUM_LENGTH = 200;

export enum IndividualBookmarkOrderField {
  DateBookmarked = "DateBookmarked",
  Name = "Name",
}

const DEFAULT_ORDER_FIELD = IndividualBookmarkOrderField.DateBookmarked;
const DEFAULT_ORDER_DIRECTION = OrderDirection.Descending;

type IndividualBookmarkQueryValues = {
  paginationParameters?: PaginationParameters;
  orderBy?: IndividualBookmarkOrderField;
  orderDir?: OrderDirection;
  query?: string;
};

/**
 * The arguments of `GET marketplace/individual-bookmarks`: one page of the caller's individual
 * bookmarks, searched and ordered by the server. The default is the newest bookmark first.
 *
 * A name order with no direction is ascending (A to Z). The server's default direction belongs to
 * the default field only, so the search params leave out the order only when it is that default
 * (`DateBookmarked`, `Descending`), and always send the direction of any other field.
 */
export default class IndividualBookmarkQuery {
  public readonly paginationParameters: PaginationParameters;
  public readonly orderBy: IndividualBookmarkOrderField;
  public readonly orderDir: OrderDirection;
  public readonly query?: string;

  public get searchParams(): URLSearchParams {
    return new URLSearchParams([...this.paginationParameters.searchParams, ...this.cacheParams]);
  }

  /** Every argument except the page, so the pages of one search share one key. */
  public get cacheKey(): string {
    return new URLSearchParams(this.cacheParams).toString();
  }

  public constructor(values: IndividualBookmarkQueryValues = {}) {
    const paginationParameters = values.paginationParameters ?? PaginationParameters.DEFAULT;
    if (
      !(paginationParameters instanceof PaginationParameters) ||
      !Number.isInteger(paginationParameters.pageIndex) || paginationParameters.pageIndex < 0 ||
      !Number.isInteger(paginationParameters.pageSize) || paginationParameters.pageSize < 1 ||
      paginationParameters.pageSize > 100
    ) {
      throw new Error("Valid pagination parameters are required.");
    }
    if (values.orderBy && !Object.values(IndividualBookmarkOrderField).includes(values.orderBy)) {
      throw new Error("A valid individual bookmark order field is required.");
    }
    if (values.orderDir && !Object.values(OrderDirection).includes(values.orderDir)) {
      throw new Error("A valid order direction is required.");
    }
    const query = values.query?.trim();
    if (query && query.length > INDIVIDUAL_BOOKMARK_QUERY_MAXIMUM_LENGTH) {
      throw new Error(`query cannot exceed ${INDIVIDUAL_BOOKMARK_QUERY_MAXIMUM_LENGTH} characters.`);
    }

    const orderBy = values.orderBy ?? DEFAULT_ORDER_FIELD;
    this.paginationParameters = paginationParameters;
    this.orderBy = orderBy;
    this.orderDir = values.orderDir ?? (orderBy === DEFAULT_ORDER_FIELD ? DEFAULT_ORDER_DIRECTION : OrderDirection.Ascending);
    this.query = query || undefined;
  }

  public withPagination(paginationParameters: PaginationParameters): IndividualBookmarkQuery {
    return new IndividualBookmarkQuery({
      paginationParameters,
      orderBy: this.orderBy,
      orderDir: this.orderDir,
      query: this.query,
    });
  }

  public isEqualTo(other?: IndividualBookmarkQuery): boolean {
    if (!other) return false;
    return this.cacheKey === other.cacheKey && this.paginationParameters.isEqualTo(other.paginationParameters);
  }

  private get cacheParams(): string[][] {
    const isDefaultField = this.orderBy === DEFAULT_ORDER_FIELD;
    const isDefaultOrder = isDefaultField && this.orderDir === DEFAULT_ORDER_DIRECTION;
    return [
      ...(isDefaultField ? [] : [["orderBy", this.orderBy]]),
      ...(isDefaultOrder ? [] : [["orderDir", this.orderDir]]),
      ...(this.query ? [["query", this.query]] : []),
    ];
  }
}
